import { useState, useCallback } from 'react';
import { getHighestAccuracyPosition, getUserIP, getUserAgent } from '../utils';
import { saveUserGeolocation, getAddressByCoords } from '../services/geolocationService';
import type { GeolocationPosition, GeolocationError } from '../types';

interface UseGeolocationResult {
  position: GeolocationPosition | null;
  error: GeolocationError | null;
  isLoading: boolean;
  requestGeolocation: (username: string, withRetry?: boolean) => Promise<boolean>;
}

/**
 * Хук для работы с Geolocation API
 * Запрашивает координаты с МАКСИМАЛЬНОЙ точностью, сохраняет их на сервер
 * Включает множественные попытки для достижения лучшей точности
 */
export function useGeolocation(): UseGeolocationResult {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Запрашивает геолокацию с множественными попытками для максимальной точности
   */
  const requestGeolocation = useCallback(
    async (username: string, withRetry: boolean = true): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      console.log('🎯 Начинаем получение координат с максимальной точностью...');
      console.log('💡 Совет: Для лучшей точности включите GPS и выйдите на открытое пространство');

      try {
        // Используем улучшенную функцию с множественными попытками
        // Это делает 3 попытки и выбирает самую точную
        const geoPosition = await getHighestAccuracyPosition(withRetry ? 3 : 1);

        setPosition(geoPosition);

        // Логируем информацию о точности
        if (geoPosition.accuracy > 100) {
          console.warn(`⚠️ ВНИМАНИЕ: Низкая точность (${geoPosition.accuracy.toFixed(2)}м).`);
          console.warn('📱 Рекомендации:');
          console.warn('  1. Включите GPS на устройстве');
          console.warn('  2. Выйдите на улицу (не в помещении)');
          console.warn('  3. Дождитесь стабилизации сигнала GPS');
        } else if (geoPosition.accuracy > 50) {
          console.log(`⚠️ Средняя точность: ${geoPosition.accuracy.toFixed(2)}м`);
        } else {
          console.log(`🎯 Отличная точность: ${geoPosition.accuracy.toFixed(2)}м`);
        }

        // КРИТИЧНО: Если точность > 30м - НЕ сохраняем, переключаемся на QR
        if (geoPosition.accuracy > 30) {
          console.warn(`🚫 Точность слишком низкая (${geoPosition.accuracy.toFixed(2)}м > 30м)`);
          console.warn('📱 Переключаемся на QR-код для использования GPS смартфона');
          setIsLoading(false);
          // Возвращаем false чтобы показать QR-код
          // НЕ сохраняем координаты с низкой точностью
          return false;
        }

        // Точность хорошая (≤ 30м) - сохраняем

        // Получаем дополнительные данные (опционально)
        const ipAddress = await getUserIP();
        const userAgent = getUserAgent();
        const guid = localStorage.getItem('guid'); // GUID пользователя из localStorage

        // Пробуем получить адрес (reverse geocoding) - опционально
        let address: string | null = null;
        try {
          const addressResponse = await getAddressByCoords(
            geoPosition.latitude,
            geoPosition.longitude
          );
          if (addressResponse.success && addressResponse.address) {
            address = addressResponse.address;
            console.log(`📍 Адрес: ${address}`);
          }
        } catch (addrError) {
          console.warn('⚠️ Не удалось получить адрес, продолжаем без него');
        }

        // Сохраняем геопозицию на сервер
        await saveUserGeolocation({
          username,
          latitude: geoPosition.latitude,
          longitude: geoPosition.longitude,
          accuracy: geoPosition.accuracy,
          address,
          ip_address: ipAddress,
          user_agent: userAgent,
          guid
        });

        console.log(' Геопозиция успешно получена и сохранена на сервере');
        setIsLoading(false);
        return true;

      } catch (err) {
        const geoError = err as GeolocationError;
        console.error(' Ошибка при получении геопозиции:', geoError);
        setError(geoError);
        setIsLoading(false);
        return false;
      }
    },
    []
  );

  return {
    position,
    error,
    isLoading,
    requestGeolocation
  };
}

